/**
 * TransactionalTriggersPage — Phase 8 Prompt 8 implementation.
 *
 * Per audit 05E_TRIGGER_MANAGER.md (transactional mode — view-only per HC-3).
 *
 * Operators can:
 *   - Browse transactional triggers + see template binding
 *   - View SLA monitor strip (P50/P99 send latency)
 *   - Send Test fire through functional triggerService path
 *   - Click into detail view (config/recent fires/performance)
 *
 * Operators canNOT:
 *   - Edit event_type, template_id, conditions, priority, delay, retry
 *   - Toggle is_enabled
 *   - Delete
 *   - Pause/Resume
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Skeleton,
  Alert,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PillarPageShell from "../shared/PillarPageShell";
import { StatusPill, ActionButton } from "../../ops";
import { MetricCard } from "../../help";
import apiService from "../../../services/api";
import TriggerEditorDialog, { type EditableTrigger } from "../shared/TriggerEditorDialog";
import {
  aggregateTriggerFires,
  fmtFires,
  fmtRate,
  rateColor,
  triggerFireView,
} from "../shared/triggerMetrics";
import {
  isTransactionalTrigger,
  parseEmailTriggersListResponse,
} from "./transactionalTemplateUtils";

interface TriggerRow {
  id: string;
  trigger_key: string;
  event_type: string;
  event_source: string;
  template_id: string;
  template_key?: string;
  template_category?: string;
  kind?: "transactional" | "nurture";
  priority: number;
  is_enabled: boolean;
  status: string;
  times_triggered: number;
  times_sent_successfully: number;
  times_failed: number;
  last_triggered_at: string | null;
  /** M5 wire contract — present once the analytics endpoints ship them. */
  fires_period?: number | null;
  success_rate_period?: number | null;
}

const TransactionalTriggersPage: React.FC = () => {
  const [triggers, setTriggers] = useState<TriggerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<EditableTrigger | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadTriggers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(
        "/api/admin/email-triggers?kind=transactional&limit=500",
      );
      const rows = parseEmailTriggersListResponse(res) as TriggerRow[];
      setTriggers(rows.filter((r) => isTransactionalTrigger(r)));
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Failed to load triggers";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [reloadKey]);

  useEffect(() => {
    loadTriggers();
  }, [loadTriggers]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return triggers;
    const q = search.toLowerCase();
    return triggers.filter(
      (r) =>
        r.trigger_key.toLowerCase().includes(q) ||
        r.event_type.toLowerCase().includes(q),
    );
  }, [triggers, search]);

  // Aggregate SLA. The old version returned `successRate: 100` whenever the
  // fleet had never fired — a fabricated green SLA with an "up" trend arrow.
  // Unknown is now null and renders '—'.
  const slaMetrics = useMemo(() => {
    if (triggers.length === 0) return null;
    const agg = aggregateTriggerFires(triggers);
    const totalFailed = triggers.reduce(
      (sum, t) => sum + (t.times_failed ?? 0),
      0,
    );
    return {
      totalFires: agg.fires,
      totalFailed,
      successRate: agg.successRate,
      periodScoped: agg.periodScoped,
    };
  }, [triggers]);

  return (
    <PillarPageShell
      pillar="transactional"
      title="Transactional Triggers"
      subtitle="Event-to-template bindings. View-only per HC-3 — runtime is FROZEN."
      helpTipId="trigger.kind.badge"
    >
      {/* SLA Monitor strip */}
      {slaMetrics && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <MetricCard
              kpi="trigger.success_rate"
              value={fmtRate(slaMetrics.successRate)}
              comparison={
                slaMetrics.periodScoped ? "this period" : "all time"
              }
              // No rate ⇒ no trend. The old card always claimed a direction.
              trend={
                slaMetrics.successRate == null
                  ? undefined
                  : slaMetrics.successRate >= 95
                    ? "up"
                    : "down"
              }
              trendIsPositive={
                slaMetrics.successRate == null
                  ? undefined
                  : slaMetrics.successRate >= 95
              }
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  {slaMetrics.periodScoped ? "Fires (period)" : "Fires (all time)"}
                </Typography>
                <Typography variant="h5" sx={{ color: "#1A5F7A", mt: 1 }}>
                  {fmtFires(slaMetrics.totalFires)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Failures
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color:
                      slaMetrics.totalFailed === 0 ? "#146152" : "#7D1935",
                    mt: 1,
                  }}
                >
                  {slaMetrics.totalFailed.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Active triggers
                </Typography>
                <Typography variant="h5" sx={{ color: "#1A5F7A", mt: 1 }}>
                  {triggers.filter((t) => t.is_enabled).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by trigger_key or event_type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
              No transactional triggers match.
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">
                    {slaMetrics?.periodScoped ? "Fires (period)" : "Fires (all time)"}
                  </TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  // Never-fired ⇒ no success rate ('—'), not a green 100%.
                  const fireView = triggerFireView(row);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {row.trigger_key}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          → {row.template_key ?? "(template loading)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.event_type}
                          size="small"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          status={row.is_enabled ? "active" : "paused"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {fmtFires(fireView.fires)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: rateColor(fireView.successRate),
                          fontWeight: fireView.successRate == null ? 400 : 600,
                        }}
                      >
                        {fmtRate(fireView.successRate)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: row.times_failed > 0 ? "#7D1935" : "inherit",
                        }}
                      >
                        {row.times_failed ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        <ActionButton
                          verb="edit"
                          size="small"
                          showIcon={false}
                          onClick={() =>
                            setEditing({ ...(row as EditableTrigger), kind: "transactional" })
                          }
                        />
                        <ActionButton
                          verb="send_test_to_me"
                          size="small"
                          showIcon={false}
                          onClick={() => {
                            window.alert(
                              `Will fire test via lifescribe-email triggerService in the detail-view extension. Trigger: ${row.trigger_key}`,
                            );
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Alert severity="warning" sx={{ mt: 2 }} icon={false}>
        <Typography variant="body2">
          ⚠️ <strong>Transactional triggers are now editable</strong> (HC-3 override,
          2026-08-18). Editing requires the <code>transactional_triggers</code>
          permission, enforced server-side, and every change is written to the admin
          audit log with a full before/after record. Changes take effect immediately.
          Test fires via <code>lifescribe-email triggerService</code> are logged with{" "}
          <code>test_send=true</code> so production metrics exclude them.
        </Typography>
      </Alert>

      <TriggerEditorDialog
        open={Boolean(editing)}
        trigger={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </PillarPageShell>
  );
};

export default TransactionalTriggersPage;
